import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {useAuth} from '@fitro/customer-host/auth'
import {
    CustomerPhoneResponse,
    CustomerProfile,
    customerProfileApiAvailable,
    getCustomerProfile,
    linkCustomerToPartner,
    updateCustomerPhone,
    updateCustomerProfile,
    UpdateCustomerPhoneRequest,
    UpdateCustomerProfileRequest
} from '../services/profile.service'
import {ApiError} from '../services/api-helpers'
import {customerHomeBootstrapKeys, customerProfileKeys} from '../lib/queryKeys'

interface UseCustomerProfileOptions {
    enabled?: boolean
}

export const useCustomerProfile = (options: UseCustomerProfileOptions = {}) =>
    {
        const {isLoaded, isSignedIn} = useAuth()
        return useQuery<CustomerProfile, ApiError>({
            queryKey: customerProfileKeys.all,
            enabled: (options.enabled ?? true) && customerProfileApiAvailable && isLoaded && isSignedIn,
            retry: (failureCount, error) => {
                // Avoid hammering on auth failures
                if (error.status === 401 || error.status === 403) return false
                return failureCount < 2
            },
            queryFn: async () => {
                try {
                    return await getCustomerProfile()
                } catch (error) {
                    // Auto-link on 404 then retry once
                    if (error instanceof ApiError && error.status === 404) {
                        await linkCustomerToPartner()
                        return await getCustomerProfile()
                    }
                    throw error
                }
            }
        })
    }

export const useUpdateCustomerProfile = () => {
    const client = useQueryClient()
    return useMutation({
        mutationFn: async (body: UpdateCustomerProfileRequest) => {
            return updateCustomerProfile(body)
        },
        onSuccess: () => {
            client.invalidateQueries({queryKey: customerProfileKeys.all})
            client.invalidateQueries({queryKey: customerHomeBootstrapKeys.all})
        }
    })
}

export const useUpdateCustomerPhone = () => {
    const client = useQueryClient()
    return useMutation({
        mutationFn: async (body: UpdateCustomerPhoneRequest) => {
            return updateCustomerPhone(body)
        },
        onSuccess: (_data: CustomerPhoneResponse) => {
            client.invalidateQueries({queryKey: customerProfileKeys.all})
            client.invalidateQueries({queryKey: customerHomeBootstrapKeys.all})
        }
    })
}
